// ========== Shared Types ==========

export type ClassificationEventName =
    | 'OnSend'
    | 'OnClassificationChange'
    | 'OnDocClassificationChange'
    | 'OnNewMessage'
    | 'OnNew'
    | 'OnStart'
    | 'OnOpen';

export type ClassificationEventType =
    | 'ApplyDocumentMarkings'
    | 'ApplyHtmlHeadersFooters'
    | 'ApplyAdvancedHeadersFooters'
    | 'ApplyPortionMarking'
    | 'ApplyBodyTag'
    | 'ApplySubjectLabel'
    | 'ApplyCustomAction'
    | 'ApplyCustomXHeader'
    | 'ApplyCustomProperty'
    | 'Alert'
    | 'RecipientAlert'
    | 'SetClassification'
    | 'EmailSent'
    | 'EmailSendAnyway'
    | 'EmailSendPrevented'
    | 'ChangeAllowed'
    | 'ChangePrevented'
    | 'Unknown';

export type SeverityLevel =
    | 'Emergency'
    | 'Alert'
    | 'Critical'
    | 'Error'
    | 'Warning'
    | 'Notice'
    | 'Informational'
    | 'Debug';

// ========== Reusable Components ==========

export interface MetadataValue {
    Value: string;
}

export interface MetadataField {
    FieldName: string;
    Values: MetadataValue[];
}

export type Metadata = MetadataField[];

export interface EnvironmentInfo {
    Host: string;
    Platform: string;
    Version: string;
}

export interface Identity {
    Address: string;
    DisplayName: string;
}

// ========== Base Event Context ==========

export interface BaseContext {
    Event: ClassificationEventName;
    EventType?: ClassificationEventType;
    Product: string;
    Version: string;
    Platform: string;
    Severity: SeverityLevel;
}

// ========== Message Event Types ==========

export interface MessageContext extends BaseContext {
    UserName: string;
}

export interface EmailRecipient extends Identity {
    RecipientType: 'to' | 'cc' | 'bcc';
    IsDistributionList: boolean;
}

export interface EmailAttachment {
    Type: 'file' | 'item' | 'cloud' | 'unknown';
    Name: string;
    Size: number;
    Metadata?: Metadata;
}

export interface EmailMessage {
    Type: 'message' | 'appointment' | 'unknown';
    Subject: string;
    Metadata: Metadata;
    OriginalMetadata?: Metadata;
    OriginalField?: Metadata;
    NewField?: Metadata;
    Sender: Identity;
    Recipients: EmailRecipient[];
    Attachments: EmailAttachment[];
    AttachmentCount?: number;
}

export interface MessageEvent {
    MessageContext: MessageContext;
    Message: EmailMessage;
}

// ========== Document Event Types ==========

export interface DocumentContext extends BaseContext {
    UserName?: string;
    Computer?: string;
}

export interface OfficeDocument {
    Filename: string;
    Path?: string;
    DocumentType: string;
    Metadata: Metadata;
    OriginalMetadata?: Metadata;
    OriginalField?: Metadata;
    NewField?: Metadata;
}

export interface DocumentEvent {
    DocumentContext: DocumentContext;
    Document: OfficeDocument;
}

// ========== Audit Event Root ==========

export interface BaseAuditEvent {
    id: string;
    TenantId: string;
    UtcDateTime: string; // ISO 8601
    Environment: EnvironmentInfo;
}

export interface MessageAuditEvent extends BaseAuditEvent {
    MessageEvent: MessageEvent;
    DocumentEvent?: never;
}

export interface DocumentAuditEvent extends BaseAuditEvent {
    DocumentEvent: DocumentEvent;
    MessageEvent?: never;
}

export type AuditEvent = MessageAuditEvent | DocumentAuditEvent;
export type EventPayload = DocumentEvent | MessageEvent;

// ========== AuditEvent Type-Guards ==========

/**
 * Narrow an AuditEvent to a MessageAuditEvent if it has a MessageEvent property.
 */
export function isMessageAuditEvent(event: AuditEvent): event is MessageAuditEvent {
    return 'MessageEvent' in event;
}

/**
 * Narrow an AuditEvent to a DocumentAuditEvent if it has a DocumentEvent property.
 */
export function isDocumentAuditEvent(event: AuditEvent): event is DocumentAuditEvent {
    return 'DocumentEvent' in event;
}

/**
 * Narrow an EventPayload to a MessageEvent if it has a MessageContext property.
 */
export function isMessagePayload(event: EventPayload): event is MessageEvent {
    return 'MessageContext' in event;
}

/**
 * Narrow an EventPayload to a DocumentEvent if it has a DocumentContext property.
 */
export function isDocumentPayload(event: EventPayload): event is DocumentEvent {
    return 'DocumentContext' in event;
}
